/**
 * Dashboard Store
 *
 * Store MobX para el sistema de widgets del dashboard.
 * Maneja la carga, configuración y datos de widgets.
 */

import { makeAutoObservable, runInAction } from 'mobx';
import { restFetch } from '../../api/apiClient';

// RootStore se recibe en constructor para compatibilidad con el patrón de stores
type RootStore = unknown;

/**
 * Posición de un widget en el grid
 */
export interface WidgetPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Configuración de un widget disponible
 */
export interface AvailableWidget {
  widgetKey: string;
  title: string;
  category: string;
  component: string;
  defaultConfig: Record<string, unknown>;
  minWidth?: number;
  minHeight?: number;
  userConfig?: {
    enabled: boolean;
    position: WidgetPosition;
    configOverride?: Record<string, unknown>;
  };
}

/**
 * Datos cargados para un widget
 */
export interface WidgetData {
  data: unknown;
  metadata?: {
    fetchedAt: string;
    cacheKey?: string;
  };
}

/**
 * Estado de carga de un widget
 */
export interface WidgetLoadingState {
  loading: boolean;
  /** El server respondió 202: computando en background — mostrar espera y repreguntar. */
  computing: boolean;
  error: string | null;
  lastFetch: number | null;
}

/** No refetchear al re-montar si los datos tienen menos que esto. El
 * server cachea con invalidación por evento, así que "reciente" acá es
 * solo para no repetir requests al navegar ida y vuelta. El botón de
 * refresh manual siempre fuerza. */
const CLIENT_FRESHNESS_MS = 60 * 1000;

/** Backoff del poll mientras un widget está 'computing'. */
const COMPUTING_POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 13000, 21000, 30000];

export class DashboardStore {
  // Estado observable
  widgets: AvailableWidget[] = [];
  widgetData: Map<string, WidgetData> = new Map();
  loadingStates: Map<string, WidgetLoadingState> = new Map();
  loading: boolean = false;
  error: string | null = null;

  // No-observables: dedup de requests en vuelo y timers de poll por widget
  private inFlight: Set<string> = new Set();
  private pollTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private pollAttempts: Map<string, number> = new Map();

  constructor(_rootStore: RootStore) {
    makeAutoObservable(this, {
      widgetData: true,
      loadingStates: true,
    });
  }

  /**
   * Widgets habilitados para mostrar en el dashboard
   */
  get enabledWidgets(): AvailableWidget[] {
    return this.widgets.filter(
      (w) => w.userConfig?.enabled !== false // Mostrar si no está explícitamente deshabilitado
    );
  }

  /**
   * Widgets agrupados por categoría
   */
  get widgetsByCategory(): Map<string, AvailableWidget[]> {
    const grouped = new Map<string, AvailableWidget[]>();
    for (const widget of this.enabledWidgets) {
      const category = widget.category || 'General';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(widget);
    }
    return grouped;
  }

  /**
   * Carga la lista de widgets disponibles y PREFETCHEA los datos de
   * todos los habilitados en UN solo request batch (la carga del
   * dashboard son 2 requests en total, no N+1).
   */
  async fetchWidgets(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const data = await restFetch<{ widgets: AvailableWidget[] }>('/api/dashboard/widgets');

      runInAction(() => {
        this.widgets = data.widgets || [];
        this.loading = false;
      });

      const keys = this.enabledWidgets
        .map((w) => w.widgetKey)
        .filter((k) => !this.isFresh(k) && !this.inFlight.has(k));
      if (keys.length > 0) {
        await this.fetchWidgetsBatch(keys);
      }
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Unknown error';
        this.loading = false;
      });
      console.error('[DashboardStore] Error fetching widgets:', err);
    }
  }

  /** ¿Los datos del widget son recientes (no hace falta refetchear al montar)? */
  private isFresh(widgetKey: string): boolean {
    const state = this.loadingStates.get(widgetKey);
    return (
      !!state?.lastFetch &&
      !state.error &&
      this.widgetData.has(widgetKey) &&
      Date.now() - state.lastFetch < CLIENT_FRESHNESS_MS
    );
  }

  private setState(widgetKey: string, patch: Partial<WidgetLoadingState>): void {
    const current = this.loadingStates.get(widgetKey) || {
      loading: false,
      computing: false,
      error: null,
      lastFetch: null,
    };
    this.loadingStates.set(widgetKey, { ...current, ...patch });
  }

  /** Aplica una entrada del batch / respuesta individual al estado. */
  private applyResult(widgetKey: string, result: unknown): void {
    const r = result as {
      status?: string;
      error?: string;
      data?: unknown;
    };
    if (r?.status === 'computing') {
      this.setState(widgetKey, { loading: false, computing: true, error: null });
      this.schedulePoll(widgetKey);
      return;
    }
    if (r?.error !== undefined) {
      this.setState(widgetKey, {
        loading: false,
        computing: false,
        error: r.error,
        lastFetch: Date.now(),
      });
      return;
    }
    this.widgetData.set(widgetKey, result as WidgetData);
    this.setState(widgetKey, {
      loading: false,
      computing: false,
      error: null,
      lastFetch: Date.now(),
    });
    this.stopPolling(widgetKey);
  }

  /**
   * Batch: datos de varios widgets en un request. Errores por-widget
   * vienen dentro de cada entrada.
   */
  private async fetchWidgetsBatch(keys: string[]): Promise<void> {
    runInAction(() => {
      for (const k of keys) this.setState(k, { loading: true, error: null });
    });
    keys.forEach((k) => this.inFlight.add(k));

    try {
      const data = await restFetch<{ results: Record<string, unknown> }>(
        '/api/dashboard/widgets/data',
        {
          method: 'POST',
          body: JSON.stringify({ requests: keys.map((widgetKey) => ({ widgetKey })) }),
        }
      );
      runInAction(() => {
        for (const k of keys) {
          this.applyResult(k, data.results?.[k] ?? { error: 'Sin datos' });
        }
      });
    } catch (err) {
      runInAction(() => {
        for (const k of keys) {
          this.setState(k, {
            loading: false,
            computing: false,
            error: err instanceof Error ? err.message : 'Unknown error',
            lastFetch: Date.now(),
          });
        }
      });
      console.error('[DashboardStore] Error fetching widgets batch:', err);
    } finally {
      keys.forEach((k) => this.inFlight.delete(k));
    }
  }

  /**
   * Asegura los datos de un widget al montar: si son recientes o ya hay
   * un request en vuelo, no hace nada. El refetch real solo ocurre al
   * volver tras un rato o vía refresh manual.
   */
  ensureWidgetData(widgetKey: string): void {
    if (this.isFresh(widgetKey) || this.inFlight.has(widgetKey)) return;
    const state = this.loadingStates.get(widgetKey);
    if (state?.computing) return; // ya hay un poll programado
    void this.fetchWidgetData(widgetKey);
  }

  /** Poll con backoff mientras el server responde 'computing'. */
  private schedulePoll(widgetKey: string): void {
    if (this.pollTimers.has(widgetKey)) return;
    const attempt = this.pollAttempts.get(widgetKey) ?? 0;
    if (attempt >= COMPUTING_POLL_DELAYS_MS.length) {
      // Presupuesto agotado: dejar de esperar, mostrar el refresh manual.
      this.pollAttempts.delete(widgetKey);
      runInAction(() => {
        this.setState(widgetKey, {
          computing: false,
          error: 'La consulta está tardando; probá actualizar en un momento.',
          lastFetch: Date.now(),
        });
      });
      return;
    }
    const delay = COMPUTING_POLL_DELAYS_MS[attempt];
    this.pollAttempts.set(widgetKey, attempt + 1);
    const timer = setTimeout(() => {
      this.pollTimers.delete(widgetKey);
      void this.fetchWidgetData(widgetKey, {}, { silent: true });
    }, delay);
    this.pollTimers.set(widgetKey, timer);
  }

  private stopPolling(widgetKey: string): void {
    const timer = this.pollTimers.get(widgetKey);
    if (timer) clearTimeout(timer);
    this.pollTimers.delete(widgetKey);
    this.pollAttempts.delete(widgetKey);
  }

  /**
   * Carga los datos de un widget específico (refresh manual y polls).
   */
  async fetchWidgetData(
    widgetKey: string,
    queryParams: Record<string, string> = {},
    opts: { silent?: boolean } = {}
  ): Promise<void> {
    if (this.inFlight.has(widgetKey)) return;
    this.inFlight.add(widgetKey);
    if (!opts.silent) this.pollAttempts.delete(widgetKey); // refresh manual resetea el backoff

    runInAction(() => {
      this.setState(widgetKey, { loading: !opts.silent, error: null });
    });

    try {
      const params = new URLSearchParams(queryParams);
      const endpoint = `/api/dashboard/widgets/${widgetKey}/data?${params}`;

      const data = await restFetch<WidgetData>(endpoint);

      runInAction(() => {
        this.applyResult(widgetKey, data);
      });
    } catch (err) {
      runInAction(() => {
        this.setState(widgetKey, {
          loading: false,
          computing: false,
          error: err instanceof Error ? err.message : 'Unknown error',
          lastFetch: Date.now(),
        });
      });
      console.error(
        `[DashboardStore] Error fetching data for widget ${widgetKey}:`,
        err
      );
    } finally {
      this.inFlight.delete(widgetKey);
    }
  }

  /**
   * Actualiza la configuración de un widget
   */
  async updateWidgetConfig(
    widgetKey: string,
    config: {
      enabled?: boolean;
      position?: WidgetPosition;
      configOverride?: Record<string, unknown>;
      displayOrder?: number;
    }
  ): Promise<void> {
    try {
      await restFetch(`/api/dashboard/widgets/${widgetKey}/config`, {
        method: 'PUT',
        body: JSON.stringify(config),
      });

      // Actualizar el widget local
      runInAction(() => {
        const widget = this.widgets.find((w) => w.widgetKey === widgetKey);
        if (widget) {
          widget.userConfig = {
            ...widget.userConfig,
            enabled: config.enabled ?? widget.userConfig?.enabled ?? true,
            position: config.position ?? widget.userConfig?.position ?? { x: 0, y: 0, width: 2, height: 2 },
            configOverride:
              config.configOverride ?? widget.userConfig?.configOverride,
          };
        }
      });
    } catch (err) {
      console.error(
        `[DashboardStore] Error updating widget config ${widgetKey}:`,
        err
      );
      throw err;
    }
  }

  /**
   * Obtiene los datos de un widget
   */
  getWidgetData(widgetKey: string): WidgetData | undefined {
    return this.widgetData.get(widgetKey);
  }

  /**
   * Obtiene el estado de carga de un widget
   */
  getLoadingState(widgetKey: string): WidgetLoadingState {
    return (
      this.loadingStates.get(widgetKey) || {
        loading: false,
        computing: false,
        error: null,
        lastFetch: null,
      }
    );
  }

}
